import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ApiError, api } from '../api/client.js'
import type { Task } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { assigneeLabel } from '../components/people.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Spinner, StatusBadge } from '../components/ui.js'
import { formatDate, formatDateTime, formatWeekday, todayIso } from '../lib/format.js'
import { BulkBar, EDITABLE } from './TaskBulk.js'
import { TaskModal } from './TaskDialogs.js'

export type SortKey = 'due' | 'completed' | 'status' | 'assignee' | 'place' | 'title' | 'unit' | 'address' | 'result'
export type Dir = 'asc' | 'desc'
export type GroupBy = 'assignee' | 'site' | 'city'

/** Dates start with the newest, names with A. */
export const FIRST_DIR: Record<SortKey, Dir> = {
  due: 'desc',
  completed: 'desc',
  status: 'asc',
  assignee: 'asc',
  place: 'asc',
  title: 'asc',
  unit: 'asc',
  address: 'asc',
  result: 'asc',
}
const AZ: [string, string] = ['A bis Z', 'Z bis A']
const SORT_HINT: Record<SortKey, [string, string]> = {
  due: ['älteste zuerst', 'neueste zuerst'],
  completed: ['älteste zuerst', 'neueste zuerst'],
  status: ['überfällig zuerst', 'erledigt zuerst'],
  assignee: AZ,
  place: AZ,
  title: AZ,
  unit: AZ,
  address: AZ,
  result: AZ,
}

export const GROUP_LABEL: Record<GroupBy, string> = { assignee: 'Mitarbeiter', site: 'Liegenschaft', city: 'Ort' }

const PAGE = 100

export const HINTS: { value: NonNullable<Task['hint']>; label: string; badge: string }[] = [
  { value: 'nicht_zugewiesen', label: 'nicht zugewiesen', badge: 'missed' },
  { value: 'krank', label: 'krank', badge: 'warn' },
  { value: 'urlaub', label: 'Urlaub', badge: 'warn' },
  { value: 'vertretung', label: 'Vertretung', badge: 'info' },
  { value: 'rueckruf', label: 'Rückruf', badge: 'warn' },
]

const building = (task: Task) => task.levels?.find((l) => l.kind === 'building')?.name ?? null
const unit = (task: Task) =>
  task.levels?.filter((l) => l.kind === 'unit').map((l) => l.name).join(' › ') || task.unitLabel || null
const dash = <span className="muted">–</span>
const zipCity = (task: Task) => [task.zip, task.city].filter(Boolean).join(' ')

export interface Column {
  key: string
  label: string
  sort?: SortKey
  className?: string
  /** Shown unless the person switched it off. */
  standard?: boolean
  render: (task: Task, today: string) => ReactNode
}

const dueColumn = (label: string): Column => ({
  key: 'due',
  label,
  sort: 'due',
  className: 'nowrap',
  standard: true,
  render: (task, today) => (
    <>
      <span className={task.dueDate === today ? 'strong' : undefined}>{formatDate(task.dueDate)}</span>
      <div className="muted small">
        {formatWeekday(task.dueDate)}
        {task.dueTime ? `, bis ${task.dueTime}` : ''}
      </div>
    </>
  ),
})
const completedColumn = (standard: boolean): Column => ({
  key: 'completed',
  label: 'Erledigt am',
  sort: 'completed',
  className: 'nowrap small',
  standard,
  render: (task) => (task.status === 'done' ? formatDateTime(task.completedAtClient) : dash),
})
const statusColumn: Column = { key: 'status', label: 'Status', sort: 'status', className: 'nowrap', standard: true, render: (task) => <StatusBadge status={task.status} /> }
const assigneeColumn: Column = {
  key: 'assignee',
  label: 'Mitarbeiter',
  sort: 'assignee',
  standard: true,
  render: (task) => {
    const h = HINTS.find((x) => x.value === task.hint)
    return (
      <>
        {assigneeLabel(task) ?? dash}
        {h && h.value !== 'rueckruf' && (
          <div>
            <span className={`badge ${h.badge}`}>{h.label}</span>
          </div>
        )}
      </>
    )
  },
}
/** Ergebnis with what follows from it: Rückruf open or geklärt, a Mangel. */
const resultColumn = (standard: boolean): Column => ({
  key: 'textOption',
  label: 'Ergebnis',
  sort: 'result',
  standard,
  render: (task) => (
    <>
      {task.textOption ?? dash}
      {(task.hint === 'rueckruf' || task.callbackResolvedAt) && (
        <div>
          {task.callbackResolvedAt ? (
            <span className="badge done" title={task.callbackNote ?? undefined}>
              Rückruf geklärt
            </span>
          ) : (
            <span className="badge warn">Rückruf</span>
          )}
        </div>
      )}
      {task.defectNote && <div className="small text-bad">Mangel: {task.defectNote}</div>}
      {task.note && <div className="muted small">{task.note}</div>}
    </>
  ),
})
const addressColumn = (standard: boolean): Column => ({
  key: 'address',
  label: 'Adresse',
  sort: 'address',
  standard,
  render: (task) =>
    task.street ? (
      <>
        {task.street}
        <div className="muted small">{zipCity(task)}</div>
      </>
    ) : (
      dash
    ),
})

/**
 * The columns of the task list. The standard set is the one from the
 * Hausmanager (Workshop 23.10.); the rest can be switched on per person.
 */
export const TASK_COLUMNS: Column[] = [
  dueColumn('Geplant am'),
  completedColumn(true),
  statusColumn,
  {
    ...assigneeColumn,
    render: (task) => {
      const h = HINTS.find((x) => x.value === task.hint)
      return (
        <>
          {assigneeLabel(task) ?? dash}
          {h && (
            <div>
              <span className={`badge ${h.badge}`}>{h.label}</span>
            </div>
          )}
        </>
      )
    },
  },
  { key: 'site', label: 'Liegenschaft', sort: 'place', standard: true, render: (task) => task.siteName },
  {
    key: 'building',
    label: 'Gebäude',
    standard: true,
    render: (task) => (
      <>
        {building(task) ?? <span className="muted">{task.objectKind === 'customer' ? 'ohne Gebäude' : 'gesamte Liegenschaft'}</span>}
        {task.unitLabel && <div className="muted small">{task.unitLabel}</div>}
      </>
    ),
  },
  { key: 'unit', label: 'Einheit', sort: 'unit', render: (task) => unit(task) ?? dash },
  {
    key: 'title',
    label: 'Leistung',
    sort: 'title',
    standard: true,
    render: (task) => (
      <>
        <span className="strong">{task.title}</span>
        {task.textOption && <div className="muted small">{task.textOption}</div>}
      </>
    ),
  },
  addressColumn(false),
  resultColumn(false),
  { key: 'note', label: 'Notiz', className: 'small', render: (task) => task.note ?? dash },
  { key: 'defectNote', label: 'Mangelmeldung', className: 'small', render: (task) => task.defectNote ?? dash },
  { key: 'externalRef', label: 'Youtilly-ID', className: 'small', render: (task) => task.externalRef ?? dash },
]

/**
 * Legionellen: the flat and its address lead, then what was found there. The
 * same keys as the task list, so the export knows them.
 */
export const LEGIONELLA_COLUMNS: Column[] = [
  { key: 'unit', label: 'Wohnung', sort: 'unit', className: 'strong', standard: true, render: (task) => unit(task) ?? dash },
  addressColumn(true),
  dueColumn('Fällig'),
  statusColumn,
  assigneeColumn,
  resultColumn(true),
  completedColumn(false),
  {
    key: 'building',
    label: 'Gebäude',
    render: (task) => building(task) ?? <span className="muted">ohne Gebäude</span>,
  },
  { key: 'defectNote', label: 'Mangelmeldung', className: 'small', render: (task) => task.defectNote ?? dash },
  { key: 'externalRef', label: 'Youtilly-ID', className: 'small', render: (task) => task.externalRef ?? dash },
]

/** Column choice per person on this device. */
export function useColumns(storageKey: string, all: Column[]) {
  const standard = all.filter((c) => c.standard).map((c) => c.key)
  const [visible, setVisible] = useState<string[]>(standard)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      const saved = raw ? (JSON.parse(raw) as string[]) : null
      setVisible(saved?.length ? saved.filter((k) => all.some((c) => c.key === k)) : standard)
    } catch {
      setVisible(standard)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])
  function toggle(key: string) {
    setVisible((current) => {
      const next = current.includes(key) ? current.filter((k) => k !== key) : all.map((c) => c.key).filter((k) => k === key || current.includes(k))
      const safe = next.length ? next : standard
      try {
        localStorage.setItem(storageKey, JSON.stringify(safe))
      } catch {
        /* storage blocked: the choice lasts for this visit */
      }
      return safe
    })
  }
  function reset() {
    try {
      localStorage.removeItem(storageKey)
    } catch {
      /* nothing saved */
    }
    setVisible(standard)
  }
  return { all, visible, shown: all.filter((c) => visible.includes(c.key)), toggle, reset }
}

export function ColumnPicker({ columns }: { columns: ReturnType<typeof useColumns> }) {
  return (
    <details className="column-picker">
      <summary className="btn secondary small">
        <Icon name="gear" size={14} /> Spalten
      </summary>
      <div className="column-menu card">
        {columns.all.map((c) => (
          <label key={c.key} className="check">
            <input type="checkbox" checked={columns.visible.includes(c.key)} onChange={() => columns.toggle(c.key)} />
            {c.label}
          </label>
        ))}
        <button type="button" className="btn ghost small" onClick={columns.reset}>
          Standard
        </button>
      </div>
    </details>
  )
}

/** Export of exactly this list with the columns on screen. */
export function exportUrl(filter: URLSearchParams, shown: Column[], format: 'xlsx' | 'csv') {
  const e = new URLSearchParams(filter)
  e.delete('groupBy')
  e.set('format', format)
  e.set('columns', shown.map((c) => c.key).join(','))
  return `/api/occurrences/export?${e}`
}

export interface ListSummary {
  total: number
  open: number
  missed: number
  done: number
  /** Counts cover only the page on screen (flat list over several pages). */
  pageOnly: boolean
  firstTitle?: string
  fetching: boolean
}

interface Group {
  key: string
  label: string
  total: number
  open: number
  missed: number
  done: number
  callback: number
}

interface TaskTableProps {
  /** The filter as the API takes it, without sort, paging and grouping. */
  filter: URLSearchParams
  columns: Column[]
  sortKey: SortKey
  dir: Dir
  onSort: (key: SortKey, dir: Dir) => void
  page: number
  onPage: (page: number) => void
  groupBy: GroupBy | null
  /** Group keys that are open. */
  openGroups: string[]
  onOpenGroups: (keys: string[]) => void
  onSummary?: (summary: ListSummary) => void
  emptyTitle?: string
  emptyHint?: string
  /** Remembers the scroll position for the way back from a plan. */
  scrollKey: string
}

/**
 * The list of tasks behind the Aufgaben and the Legionellen page: sortable
 * columns, Mehrfachauswahl up to "alle im Filter", real pages, and optionally
 * grouped by person, Liegenschaft or town with one row per group that opens.
 */
export function TaskTable(props: TaskTableProps) {
  const { filter, columns, sortKey, dir, onSort, groupBy, scrollKey } = props
  const queryClient = useQueryClient()
  const toast = useToast()
  const today = todayIso()
  const filterKey = filter.toString()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [openTask, setOpenTask] = useState<string | null>(null)
  // Ids of "alle im Filter", reaching beyond what is on screen.
  const [allInFilter, setAllInFilter] = useState(false)

  // A new filter starts with nothing ticked.
  useEffect(() => {
    setSelected(new Set())
    setAllInFilter(false)
  }, [filterKey, groupBy])

  const sorted = new URLSearchParams(filter)
  sorted.set('sort', sortKey)
  sorted.set('dir', dir)

  const flatQuery = new URLSearchParams(sorted)
  flatQuery.set('limit', String(PAGE))
  flatQuery.set('offset', String((props.page - 1) * PAGE))
  const flat = useQuery({
    queryKey: ['occurrences', 'liste', flatQuery.toString()],
    queryFn: () => api<{ tasks: Task[]; total: number }>(`/api/occurrences?${flatQuery}`),
    placeholderData: (previous) => previous,
    enabled: !groupBy,
  })

  const groupQuery = new URLSearchParams(filter)
  if (groupBy) groupQuery.set('groupBy', groupBy)
  const groups = useQuery({
    queryKey: ['occurrences', 'gruppen', groupQuery.toString()],
    queryFn: () => api<Group[]>(`/api/occurrences/groups?${groupQuery}`),
    placeholderData: (previous) => previous,
    enabled: Boolean(groupBy),
  })

  const tasks = flat.data?.tasks ?? []
  const total = groupBy ? (groups.data ?? []).reduce((sum, g) => sum + g.total, 0) : (flat.data?.total ?? 0)
  const loaded = groupBy ? groups.data !== undefined : flat.data !== undefined
  const fetching = groupBy ? groups.isFetching : flat.isFetching

  const { onSummary } = props
  useEffect(() => {
    if (!onSummary || !loaded) return
    if (groupBy) {
      const g = groups.data ?? []
      onSummary({
        total,
        open: g.reduce((s, x) => s + x.open, 0),
        missed: g.reduce((s, x) => s + x.missed, 0),
        done: g.reduce((s, x) => s + x.done, 0),
        pageOnly: false,
        fetching,
      })
    } else {
      const c = { open: 0, missed: 0, done: 0 }
      for (const t of tasks) if (t.status in c) c[t.status as keyof typeof c]++
      onSummary({ total, ...c, pageOnly: total > PAGE, firstTitle: tasks[0]?.title, fetching })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat.data, groups.data, fetching, groupBy])

  // Back from a plan or another page: the same scroll position as before.
  const restored = useRef(false)
  useLayoutEffect(() => {
    if (restored.current || !loaded) return
    restored.current = true
    try {
      const y = Number(sessionStorage.getItem(scrollKey) ?? '0')
      if (y > 0) window.scrollTo(0, y)
    } catch {
      /* nothing saved */
    }
  }, [loaded, scrollKey])
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const save = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        try {
          sessionStorage.setItem(scrollKey, String(Math.round(window.scrollY)))
        } catch {
          /* storage blocked */
        }
      }, 150)
    }
    window.addEventListener('scroll', save, { passive: true })
    return () => {
      clearTimeout(timer)
      window.removeEventListener('scroll', save)
    }
  }, [scrollKey])

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
    await queryClient.invalidateQueries({ queryKey: ['legionella'] })
  }

  /** Ticks every open task of the filter, or of one group, fetched from the server. */
  async function selectFromServer(extra?: Record<string, string>) {
    const q = new URLSearchParams(filter)
    for (const [k, v] of Object.entries(extra ?? {})) q.set(k, v)
    try {
      const result = await api<{ ids: string[] }>(`/api/occurrences/ids?${q}`)
      setSelected((current) => new Set([...current, ...result.ids]))
      if (!extra) setAllInFilter(true)
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Auswahl fehlgeschlagen.', 'error')
    }
  }

  function toggle(id: string) {
    setAllInFilter(false)
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function head(column: Column) {
    const nowrap = column.className?.includes('nowrap') ? 'nowrap' : undefined
    if (!column.sort) return <th key={column.key} className={nowrap}>{column.label}</th>
    const id = column.sort
    const active = sortKey === id
    const hint = SORT_HINT[id][active && dir === 'desc' ? 1 : 0]
    return (
      <th key={column.key} className={nowrap} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button
          type="button"
          className={`sort-head ${active ? 'active' : ''}`}
          onClick={() => onSort(id, active ? (dir === 'asc' ? 'desc' : 'asc') : FIRST_DIR[id])}
          title={active ? `Sortiert: ${hint}` : `Sortieren: ${SORT_HINT[id][FIRST_DIR[id] === 'asc' ? 0 : 1]}`}
        >
          {column.label}
          <span className="sort-arrow" aria-hidden="true">
            {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
          </span>
        </button>
      </th>
    )
  }

  const rowProps = { columns, today, selected, onToggle: toggle, onOpen: setOpenTask }
  const editableOnPage = tasks.filter((t) => EDITABLE.includes(t.status))
  const allOnPage = editableOnPage.length > 0 && editableOnPage.every((t) => selected.has(t.id))
  const ids = [...selected]
  const pages = Math.max(1, Math.ceil(total / PAGE))

  return (
    <>
      {ids.length > 0 && (
        <BulkBar
          ids={ids}
          onClear={() => {
            setSelected(new Set())
            setAllInFilter(false)
          }}
          onDone={() => {
            setSelected(new Set())
            setAllInFilter(false)
            void refresh()
          }}
        />
      )}
      {(allInFilter || (groupBy ? false : allOnPage && total > tasks.length)) && (
        <div className="select-all-note small">
          {allInFilter ? (
            <>
              Alle {ids.length} offenen Aufgaben im Filter sind ausgewählt.{' '}
              <button type="button" className="link-button" onClick={() => { setSelected(new Set()); setAllInFilter(false) }}>
                Auswahl aufheben
              </button>
            </>
          ) : (
            <>
              {editableOnPage.length} auf dieser Seite ausgewählt.{' '}
              <button type="button" className="link-button" onClick={() => void selectFromServer()}>
                Alle offenen im Filter auswählen
              </button>
            </>
          )}
        </div>
      )}

      {!loaded ? (
        <Spinner />
      ) : total === 0 ? (
        <EmptyState title={props.emptyTitle ?? 'Keine Aufgaben'} hint={props.emptyHint ?? 'Zeitraum oder Filter anpassen.'} />
      ) : (
        <div className="table-scroll">
          <table className="data task-table">
            <thead>
              <tr>
                <th className="check-col">
                  {groupBy ? (
                    <button type="button" className="link-button small" title="Alle offenen im Filter auswählen" onClick={() => void selectFromServer()}>
                      alle
                    </button>
                  ) : (
                    <input
                      type="checkbox"
                      aria-label="Alle offenen dieser Seite auswählen"
                      checked={allOnPage}
                      disabled={editableOnPage.length === 0}
                      onChange={() => {
                        setAllInFilter(false)
                        setSelected(allOnPage ? new Set() : new Set(editableOnPage.map((t) => t.id)))
                      }}
                    />
                  )}
                </th>
                {columns.map(head)}
              </tr>
            </thead>
            {groupBy ? (
              (groups.data ?? []).map((group) => {
                const open = props.openGroups.includes(group.key)
                return (
                  <tbody key={group.key}>
                    <GroupHead
                      group={group}
                      open={open}
                      span={columns.length + 1}
                      onToggle={() => props.onOpenGroups(open ? props.openGroups.filter((k) => k !== group.key) : [...props.openGroups, group.key])}
                      onSelect={() => void selectFromServer({ groupBy, groupKey: group.key })}
                    />
                    {open && <GroupRows params={sorted} groupBy={groupBy} groupKey={group.key} {...rowProps} />}
                  </tbody>
                )
              })
            ) : (
              <tbody>
                {tasks.map((task) => (
                  <TaskRow key={task.id} task={task} {...rowProps} />
                ))}
              </tbody>
            )}
          </table>
        </div>
      )}
      {!groupBy && total > PAGE && (
        <div className="card-body pager">
          <button type="button" className="btn secondary small" disabled={props.page <= 1 || fetching} onClick={() => props.onPage(props.page - 1)}>
            <Icon name="chevronLeft" size={14} /> Zurück
          </button>
          <span className="muted small">
            {(props.page - 1) * PAGE + 1} bis {Math.min(props.page * PAGE, total)} von {total}, Seite {props.page} von {pages}
          </span>
          <button type="button" className="btn secondary small" disabled={props.page >= pages || fetching} onClick={() => props.onPage(props.page + 1)}>
            Weiter <Icon name="chevronRight" size={14} />
          </button>
        </div>
      )}

      <TaskModal
        taskId={openTask}
        onClose={() => setOpenTask(null)}
        onChanged={(message) => {
          if (message) toast(message)
          void refresh()
        }}
      />
    </>
  )
}

interface RowProps {
  columns: Column[]
  today: string
  selected: Set<string>
  onToggle: (id: string) => void
  onOpen: (id: string) => void
}

function TaskRow({ task, columns, today, selected, onToggle, onOpen }: RowProps & { task: Task }) {
  return (
    <tr
      className={`clickable ${selected.has(task.id) ? 'selected' : ''} ${task.status === 'missed' ? 'row-missed' : ''} ${task.hint === 'rueckruf' ? 'row-callback' : ''}`}
      onClick={() => onOpen(task.id)}
    >
      <td className="check-col" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`${task.title} auswählen`}
          disabled={!EDITABLE.includes(task.status)}
          checked={selected.has(task.id)}
          onChange={() => onToggle(task.id)}
        />
      </td>
      {columns.map((c) => (
        <td key={c.key} className={c.className}>
          {c.render(task, today)}
        </td>
      ))}
    </tr>
  )
}

/** One row per group: name, how far it is, open the tasks, tick them all. */
function GroupHead({ group, open, span, onToggle, onSelect }: { group: Group; open: boolean; span: number; onToggle: () => void; onSelect: () => void }) {
  const editable = group.open + group.missed
  return (
    <tr className="group-row" onClick={onToggle}>
      <td colSpan={span}>
        <div className="group-row-inner">
          <button type="button" className="group-toggle" aria-expanded={open} onClick={(e) => { e.stopPropagation(); onToggle() }}>
            <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} />
            <span className="strong">{group.label}</span>
          </button>
          <span className="small muted">
            {group.total} {group.total === 1 ? 'Aufgabe' : 'Aufgaben'}, {group.done} erledigt
            {group.open > 0 && `, ${group.open} offen`}
          </span>
          {group.missed > 0 && <span className="badge missed">{group.missed} überfällig</span>}
          {group.callback > 0 && <span className="badge warn">{group.callback} Rückruf</span>}
          {group.total > 0 && group.done === group.total && <span className="badge done">fertig</span>}
          <span className="group-row-bar" aria-hidden="true">
            <span style={{ width: `${group.total ? Math.round((group.done / group.total) * 100) : 0}%` }} />
          </span>
          {editable > 0 && (
            <button type="button" className="link-button small" onClick={(e) => { e.stopPropagation(); onSelect() }}>
              {editable} offene auswählen
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

/** The tasks of one open group, 100 at a time. */
function GroupRows({ params, groupBy, groupKey, ...rowProps }: RowProps & { params: URLSearchParams; groupBy: GroupBy; groupKey: string }) {
  const [limit, setLimit] = useState(PAGE)
  const q = new URLSearchParams(params)
  q.set('groupBy', groupBy)
  q.set('groupKey', groupKey)
  q.set('limit', String(limit))
  q.set('offset', '0')
  const { data, isFetching } = useQuery({
    queryKey: ['occurrences', 'gruppe', q.toString()],
    queryFn: () => api<{ tasks: Task[]; total: number }>(`/api/occurrences?${q}`),
    placeholderData: (previous) => previous,
  })
  const span = rowProps.columns.length + 1
  if (!data)
    return (
      <tr>
        <td colSpan={span} className="muted small">
          wird geladen …
        </td>
      </tr>
    )
  return (
    <>
      {data.tasks.map((task) => (
        <TaskRow key={task.id} task={task} {...rowProps} />
      ))}
      {data.total > data.tasks.length && (
        <tr>
          <td colSpan={span}>
            <button type="button" className="btn secondary small" disabled={isFetching} onClick={() => setLimit((l) => l + PAGE)}>
              Weitere {Math.min(PAGE, data.total - data.tasks.length)} von {data.total - data.tasks.length} laden
            </button>
          </td>
        </tr>
      )}
    </>
  )
}
